// ContentPreview - Renders content preview based on component type

import type { ContentComponentType } from '../../../hooks/unified/useContentVersionsQueries';

interface ContentPreviewProps {
  content: string;
  componentType: ContentComponentType;
  maxLines?: number;
}

export function ContentPreview({ content, componentType, maxLines = 5 }: ContentPreviewProps) {
  // Parse content based on type
  const renderContent = () => {
    switch (componentType) {
      case 'title':
        return (
          <p className="text-sm font-medium text-gray-900 truncate">
            {content}
          </p>
        );

      case 'description':
        // Render markdown-like content (simplified)
        const lines = content.split('\n').slice(0, maxLines * 2);
        const truncated = content.split('\n').length > maxLines * 2;
        
        return (
          <div className="text-sm text-gray-700 space-y-1">
            {lines.map((line, i) => {
              if (line.startsWith('## ')) {
                return (
                  <p key={i} className="font-semibold text-gray-900 mt-2">
                    {line.replace('## ', '')}
                  </p>
                );
              }
              if (line.startsWith('- ') || line.startsWith('• ')) {
                return (
                  <p key={i} className="pl-4 text-gray-600">
                    • {line.replace(/^[-•]\s*/, '')}
                  </p>
                );
              }
              if (line.trim()) {
                return <p key={i}>{line}</p>;
              }
              return null;
            })}
            {truncated && (
              <p className="text-gray-400 italic">...more content</p>
            )}
          </div>
        );

      case 'requirements':
      case 'benefits':
        // Parse JSON array
        let items: string[] = [];
        try {
          items = JSON.parse(content);
        } catch {
          // If not valid JSON, treat as single item
          items = [content];
        }

        const displayItems = items.slice(0, maxLines);
        const hasMore = items.length > maxLines;

        return (
          <ul className="text-sm text-gray-700 space-y-1">
            {displayItems.map((item, i) => (
              <li key={i} className="flex items-start gap-2">
                <span className="text-purple-500 mt-0.5">•</span>
                <span>{item}</span>
              </li>
            ))}
            {hasMore && (
              <li className="text-gray-400 italic pl-4">
                +{items.length - maxLines} more
              </li>
            )}
          </ul>
        );

      case 'image':
        // Render image thumbnail
        return (
          <div className="rounded overflow-hidden">
            <img
              src={content.startsWith('http') || content.startsWith('data:') ? content : `${import.meta.env.VITE_API_BASE_URL || ''}${content}`}
              alt="Job image preview"
              className="w-full h-24 object-cover"
            />
          </div>
        );

      default:
        return <p className="text-sm text-gray-700">{content}</p>;
    }
  };

  return <div className="overflow-hidden">{renderContent()}</div>;
}
